import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  PlusCircle,
  ShieldAlert,
  FileText,
  Settings,
  CreditCard,
  Newspaper,
  LogOut,
  Wrench,
  BookOpen,
  Scale,
  Database,
  DollarSign,
  Users,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import useEntitlement from "../hooks/useEntitlement";
import {
  getActiveBusiness,
  getCanonicalRemediationContext,
  writeRemediationContext,
} from "../lib/auditHistory";
import { usePlatformSecurityCapabilities } from "../context/PlatformSecurityCapabilityContext.jsx";

const NAV_ITEM_BASE =
  "group relative flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-extrabold transition-all select-none";

const NAV_ITEM_INACTIVE =
  "text-slate-600 hover:text-slate-900 hover:bg-slate-100";

const NAV_ITEM_ACTIVE =
  "text-slate-900 bg-slate-900/5 ring-1 ring-slate-900/10";

function NavItem({ to, icon: Icon, label, end = false, onClick = undefined }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        [NAV_ITEM_BASE, isActive ? NAV_ITEM_ACTIVE : NAV_ITEM_INACTIVE].join(" ")
      }
    >
      <Icon size={18} />
      <span className="flex-1">{label}</span>
    </NavLink>
  );
}

function BrandMark() {
  return (
    <div className="h-10 w-10 rounded-2xl bg-blue-600/10 ring-1 ring-blue-600/15 flex items-center justify-center">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2.5L19 6.3V12.2C19 16.9 15.9 20.8 12 22.1C8.1 20.8 5 16.9 5 12.2V6.3L12 2.5Z"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    </div>
  );
}

export default function Sidebar() {
  const navigate = useNavigate();

  const entitlement = useEntitlement?.() || {};
  const entLoading = Boolean(entitlement.loading);
  const entIsTopTier = Boolean(entitlement.isTopTier);
  const isTopTier = !entLoading && entIsTopTier;

  const { status: capabilityStatus, hasCapability } =
    usePlatformSecurityCapabilities();

  const canManagePlatformSecurity =
    capabilityStatus === "ready" &&
    hasCapability("MANAGE_PLATFORM_SECURITY");

  const canManagePayouts =
    capabilityStatus === "ready" && hasCapability("MANAGE_PAYOUTS");

  const canManageAffiliates =
    capabilityStatus === "ready" &&
    hasCapability("MANAGE_AFFILIATE_POLICY_OVERRIDES");

  const paths = {
    dashboard: "/dashboard",
    startAudit: isTopTier ? "/start-audit?mode=new" : "/start-audit?mode=rerun",
    remediation: "/remediation",
    reports: "/reports",
    aiNews: "/ai-news",
    userGuide: "/user-guide",
    pricing: "/pricing",
    profile: "/profile",
    terms: "/terms",
    legal: "/legal",
    privacy: "/privacy",
    auth: "/auth",
    adminTools: "/admin-tools",
    salesAffiliatesAdmin: "/admin-tools/sales-affiliates",
    payoutsAdmin: "/admin-tools/sales-affiliate-payouts",
  };

  const handleRemediationNavigation = () => {
    try {
      const businessId = getActiveBusiness()?.business_id || "";
      const context = getCanonicalRemediationContext({ businessId });
      writeRemediationContext(context);
    } catch {
      // Navigation still proceeds; RemediationHub retains its defensive bootstrap.
    }
  };

  const handleExitSystem = async () => {
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {}

    try {
      window.location.assign(paths.auth);
    } catch {
      navigate(paths.auth, { replace: true });
    }
  };

  return (
    <aside className="w-72 flex-shrink-0 border-r border-slate-200 bg-white h-screen">
      <div className="h-full flex flex-col">
        <div className="px-6 pt-7 pb-5">
          <div className="flex items-start gap-4">
            <BrandMark />

            <div className="flex-1">
              <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-400">
                AI Fine Protector™
              </div>

              <div className="mt-2 text-2xl font-black tracking-tight text-slate-900 leading-none">
                Control Center
              </div>

              <div className="mt-2 text-xs text-slate-500">
                Governance-first AI risk visibility.
              </div>
            </div>
          </div>
        </div>

        <nav className="px-4 flex-1 overflow-y-auto pb-4">
          <div className="space-y-2">
            <NavItem to={paths.dashboard} icon={LayoutGrid} label="Dashboard" end />
            <NavItem to={paths.startAudit} icon={PlusCircle} label="New Audit" />
            <NavItem
              to={paths.remediation}
              icon={ShieldAlert}
              label="Remediation"
              onClick={handleRemediationNavigation}
            />
            <NavItem to={paths.reports} icon={FileText} label="Reports" />
          </div>

          <div className="my-5 border-t border-slate-100" />

          <div className="space-y-2">
            <div className="px-2 text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">
              Account
            </div>

            <NavItem to={paths.profile} icon={Settings} label="Account Settings" />
            <NavItem to={paths.pricing} icon={CreditCard} label="Plans" />

            <div className="my-5 border-t border-slate-100" />

            <div className="px-2 text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">
              Guidance
            </div>

            <NavItem to={paths.userGuide} icon={BookOpen} label="User Guide" />

            <div className="my-5 border-t border-slate-100" />

            <div className="px-2 text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">
              Updates
            </div>

            <NavItem to={paths.aiNews} icon={Newspaper} label="AI News" />

            <div className="my-5 border-t border-slate-100" />

            <div className="px-2 text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">
              Legal
            </div>

            <NavItem to={paths.terms} icon={FileText} label="Terms of Service" />
            <NavItem to={paths.privacy} icon={Database} label="Privacy Policy" />
            <NavItem to={paths.legal} icon={Scale} label="Legal Disclaimer" />

            {canManagePlatformSecurity || canManagePayouts || canManageAffiliates ? (
              <>
                <div className="my-5 border-t border-slate-100" />
                <div className="px-2 text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">
                  Super Admin
                </div>
                {canManageAffiliates ? (
                  <NavItem
                    to={paths.salesAffiliatesAdmin}
                    icon={Users}
                    label="Sales Affiliates"
                  />
                ) : null}
                {canManagePayouts ? (
                  <NavItem
                    to={paths.payoutsAdmin}
                    icon={DollarSign}
                    label="Affiliate Payouts"
                  />
                ) : null}
                {canManagePlatformSecurity ? (
                  <NavItem to={paths.adminTools} icon={Wrench} label="Dev Tools" />
                ) : null}
              </>
            ) : null}
          </div>
        </nav>

        <div className="px-4 py-5 border-t border-slate-100">
          <button
            type="button"
            onClick={handleExitSystem}
            className="flex items-center gap-3 text-slate-600 hover:text-red-700"
          >
            <LogOut size={18} />
            <span>Exit System</span>
          </button>

          <div className="mt-4 px-2 text-[11px] text-slate-400 leading-snug">
            © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
          </div>
        </div>
      </div>
    </aside>
  );
}
